<template>
  <transition
    appear
    name="fade"
    mode="out-in"
    :duration="{ enter: 1000, leave: 200 }"
  >
    <div class="d-flex flex-fill align-items-center">
      <b-container>
        <BaseInput
          v-model="$v.form.name.$model"
          :form="$v.form.name"
          type="text"
          :label="$t('nickname')"
          @keyup.enter.native="handleProfileUpdate"
        />

        <BaseLabel>{{ $t('email') }}</BaseLabel>
        <BaseInput
          v-if="!user.email"
          v-model="$v.form.email.$model"
          :form="$v.form.email"
          type="email"
          @keyup.enter.native="handleProfileUpdate"
        />
        <div
          v-else
          class="pl-1"
        >
          <b-badge
            v-b-tooltip.hover
            variant="primary"
            :title="$t('email-can-not-be-changed')"
          >
            {{ user.email }}
          </b-badge>
        </div>

        <b-row>
          <b-col :cols="3">
            <BaseSelect
              v-model="$v.form.phonePrefix.$model"
              :label="$t('phone')"
              :form="$v.form.phonePrefix"
              :options="countriesPhones"
            />
          </b-col>
          <b-col
            :cols="9"
            class="d-flex align-items-end"
          >
            <BaseInput
              v-model="$v.form.phone.$model"
              class="w-100"
              :form="$v.form.phone"
              type="number"
              @keyup.enter.native="handleRegisterSubmit"
            />
          </b-col>
        </b-row>

        <div class="d-flex align-items-center py-3">
          <BaseLabel>
            {{ $t('change-password-?') }}
          </BaseLabel>
          <ToggleButton
            v-model="isPasswordChangeActive"
            class="m-0 ml-3"
            color="#0cb804"
            sync
          />
        </div>

        <slide-up-down :active="isPasswordChangeActive">
          <div>
            <BaseInput
              v-model="$v.form.oldPassword.$model"
              :form="$v.form.oldPassword"
              type="password"
              :label="$t('old-password')"
              @keyup.enter.native="handleProfileUpdate"
            />

            <BaseInput
              v-model="$v.form.newPassword.$model"
              :form="$v.form.newPassword"
              type="password"
              :label="$t('new-password')"
              @keyup.enter.native="handleProfileUpdate"
            />
          </div>
        </slide-up-down>

        <BaseLabel class="pt-3">
          {{ $t('avatar') }}
        </BaseLabel>
        <TheChangeAvatar
          :url="user.avatar.image_url"
          theme="Light"
          @click="handleAvatarEditClick"
        />

        <div class="my-5 d-flex justify-content-center">
          <transition
            name="fade"
            mode="out-in"
            :duration="{ enter: 600, leave: 200 }"
          >
            <BaseLoading
              v-if="isLoading"
              :min-height="41"
            />
            <BaseButton
              v-else
              is-rounded
              :is-disabled="$v.$invalid"
              @click="handleProfileUpdate"
            >
              <span class="px-3">
                {{ $t('update-my-profile') }}
              </span>
            </BaseButton>
          </transition>
        </div>
      </b-container>
    </div>
  </transition>
</template>

<script>
import Vue from 'vue'
import ToggleButton from 'vue-js-toggle-button'
import SlideUpDown from 'vue-slide-up-down'
import { mapActions, mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required, email, minLength, maxLength } from 'vuelidate/lib/validators'
import { buildNotification } from '@/helpers/ui'
import { getCountriesSelectData } from '@/helpers/phones'
import BaseInput from '@/components/Atoms/Form/BaseInput'
import BaseButton from '@/components/Atoms/BaseButton'
import BaseLabel from '@/components/Atoms/Form/BaseLabel'
import TheChangeAvatar from '@/components/Molecules/TheChangeAvatar'
import BaseLoading from '@/components/Atoms/BaseLoading'
import BaseSelect from '@/components/Atoms/Form/BaseSelect'

Vue.use(ToggleButton)
Vue.component('slide-up-down', SlideUpDown)

export default {
  name: 'MyProfile',
  components: {
    BaseSelect,
    BaseLoading,
    TheChangeAvatar,
    BaseLabel,
    BaseButton,
    BaseInput,
  },
  mixins: [validationMixin],
  data() {
    return {
      isLoading: false,
      isPasswordChangeActive: false,
      form: {
        name: '',
        email: '',
        phone: null,
        phonePrefix: (this.env === 'cn') ? 86 : 90,
        oldPassword: '',
        newPassword: '',
      },
    }
  },
  computed: {
    ...mapState({
      isUserLoading: state => state['auth'].isUserLoading,
      user: state => state['auth'].user,
    }),
    countriesPhones() {
      return getCountriesSelectData()
    },
    env() {
      return process.env.VUE_APP_ENV
    },
  },
  validations() {
    let rules = {
      form: {
        name: {
          required,
          maxLength: maxLength(191),
        },
        email: {
          email,
          maxLength: maxLength(191),
        },
        phone: {},
        phonePrefix: {},
        oldPassword: {},
        newPassword: {},
      },
    }
    if (this.isPasswordChangeActive) {
      rules = {
        form: {
          ...rules.form,
          oldPassword: {
            required,
            minLength: minLength(6),
          },
          newPassword: {
            required,
            minLength: minLength(6),
          },
        },
      }
    }
    return rules
  },
  created() {
    this.form.name = this.user.name
    this.form.email = this.user.email
    if (this.user.phone) {
      this.form.phone = this.user.phone
      this.form.phonePrefix = this.user.phone_prefix
    }
  },
  methods: {
    ...mapActions({
      updateMyProfile: 'profile/myProfile/updateMyProfile',
      fetchUser: 'auth/fetchUser',
    }),
    handleProfileUpdate() {
      this.$v.$touch()
      if (!this.$v.$anyError) {
        this.isLoading = true
        let params = {
          name: this.form.name,
        }
        if (!this.user.email && this.form.email) {
          params.email = this.form.email
        }
        if (this.isPasswordChangeActive) {
          params.password = this.form.oldPassword
          params.new_password = this.form.oldPassword
        }
        if (this.form.phone) {
          params.phone = this.form.phone ? `+${this.form.phonePrefix}${this.form.phone}` : null
        } else {
          params.phone = null
        }
        this.updateMyProfile(params).then((res) => {
          this.isLoading = false
          if (res === 'ok') {
            this.fetchUser({ isSilent: true }).then(({ user }) => {
              this.form.name = user.name
              this.form.email = user.email
            })
            buildNotification({
              title: this.$t('your-profile-was-successfully-updated'),
            })
          }
        })
      }
    },
    handleAvatarEditClick() {
      this.$router.push({
        name: 'change-avatar',
      })
    },
  },
}
</script>

<style scoped>

</style>
